import {
  AddRounded,
  DeleteOutlineRounded,
  EditRounded,
  SearchRounded,
  UploadFileRounded,
  VisibilityRounded,
} from "@mui/icons-material";
import {
  Box,
  Button,
  CardActions,
  CardContent,
  Chip,
  Grid,
  IconButton,
  InputAdornment,
  TextField as MuiTextField,
  Tooltip,
  Typography,
  styled,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "react-query";
import MainLayout from "../../layouts/main/main_layout";
import HospitalLayout from "../../layouts/hospitals/hospital_layout";
import Loading from "../../components/loading/loading";
import useUser from "../../providers/user/user_provider";
import { useFeedback } from "../../providers/feedback/feeedback";
import { queryClient } from "../../providers/query/query";
import { deleteHospital, getHospitals } from "../../services/hospitals/hospitals";
import { hospitals_add, hospitals_edit, hospitals_view } from "../../routes/routes_path";
import { Hospital } from "./model/hospital";
import DeleteHospitalDialog from "./components/delete_hospital_dialog";
import BulkUploadHospitalsDialog from "./components/bulk_upload_hospitals/bulk_upload_hospitals_dialog";
import FilterHospitals, {
  HospitalsFilterValues,
} from "./components/filter_hospitals/filter_hospitals";
import {
  TableCell,
  TableEmptyState,
  TableFooterPagination,
  TableHeaderRow,
  TableRow,
} from "../users/components/table_toolkit/table_toolkit";
import { ToolbarButton } from "../../components/toolbar_button/toolbar_button";
import FilterIcon from "../../assets/filter_icon.svg";
import SortIcon from "../../assets/sort.svg";
import OrgSelector from "../../components/org_selector/org_selector";
import { useSelectedOrgStore } from "../../store/use-selected-org-store";
import SortHospitalsPopup, {
  DEFAULT_HOSPITALS_SORT_OPTION,
  HospitalsSortOption,
  hospitalsSortOptionLabel,
  hospitalsSortOptionToParams,
} from "./components/sort_hospitals_popup/sort_hospitals_popup";
import {
  black,
  green,
  greenLight,
  greyEnabled,
  purple,
  purpleLight,
  red,
  redLight,
} from "../../theme/colors";

const ROWS_PER_PAGE_OPTIONS = [8, 25, 50, 100];

const TextField = styled(MuiTextField)(() => ({
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#FFFFFF",
    borderRadius: 8,
    "& fieldset": { borderColor: "#ABA0AB" },
    "&:hover fieldset": { borderColor: purple },
    "&.Mui-focused fieldset": { borderColor: purple, borderWidth: 2 },
  },
}));

function capitalize(value: string) {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

const EMPTY_FILTERS: HospitalsFilterValues = {
  hospitalType: null,
  stateId: null,
  districtId: null,
  fromDate: "",
  toDate: "",
};

export default function Hospitals() {
  const navigate = useNavigate();
  const { hasPermission } = useUser();
  const { showSnackbar } = useFeedback();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(8);
  const [searchValue, setSearchValue] = useState("");
  const [debouncedSearchValue, setDebouncedSearchValue] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<Hospital | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [filters, setFilters] = useState<HospitalsFilterValues>(EMPTY_FILTERS);
  const [sortOption, setSortOption] = useState<HospitalsSortOption>(DEFAULT_HOSPITALS_SORT_OPTION);
  const [sortDialogOpen, setSortDialogOpen] = useState(false);
  const [bulkUploadDialogOpen, setBulkUploadDialogOpen] = useState(false);
  const { selectedOrgId } = useSelectedOrgStore();

  const activeFilterCount = [
    filters.hospitalType,
    filters.stateId,
    filters.districtId,
    filters.fromDate && filters.toDate,
  ].filter(Boolean).length;

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearchValue(searchValue), 500);
    return () => clearTimeout(handler);
  }, [searchValue]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearchValue, filters, selectedOrgId, sortOption, pageSize]);

  const { data: hospitalsData, isLoading, isError } = useQuery(
    ["hospitals", page, pageSize, debouncedSearchValue, filters, selectedOrgId, sortOption],
    async () => {
      const { sortBy, sortOrder } = hospitalsSortOptionToParams(sortOption);
      let response = await getHospitals({
        page,
        size: pageSize,
        search: debouncedSearchValue,
        hospital_type: filters.hospitalType || undefined,
        state_id: filters.stateId || undefined,
        district_id: filters.districtId || undefined,
        from_date: filters.fromDate || undefined,
        to_date: filters.toDate || undefined,
        orgId: selectedOrgId,
        sortBy,
        sortOrder,
      });
      return response.data.data as { total: number; list: Hospital[] };
    }
  );

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage + 1);
  };

  const handleDeleteConfirm = () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    deleteHospital(deleteTarget.id)
      .then(() => {
        showSnackbar("Hospital deleted successfully");
        queryClient.invalidateQueries({ queryKey: ["hospitals"] });
        setDeleteTarget(null);
      })
      .catch((e) => {
        showSnackbar(
          e?.response?.data?.error?.message ||
            "Something went wrong while deleting the hospital."
        );
      })
      .finally(() => setIsDeleting(false));
  };

  const count = hospitalsData?.total || 0;
  const list = hospitalsData?.list || [];

  const startRange = (page - 1) * pageSize + 1;
  const endRange = Math.min(page * pageSize, count);
  const customText = `Showing ${count ? startRange : 0} to ${endRange || 0} of ${count || 0} entries`;
  const showList = !isLoading && !isError && list.length > 0;
  const showEmpty = !isLoading && !isError && list.length === 0;

  return (
    <MainLayout>
    <HospitalLayout page={page} setPage={setPage}>
      <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          gap={2}
          mb={2}
        >
          <Box maxWidth={{ xs: "100%", sm: 360 }} flex={1} minWidth={{ xs: "100%", sm: 220 }}>
            <TextField
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              size="small"
              fullWidth
              placeholder="Search by hospital name or AMI ID"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRounded sx={{ color: greyEnabled, fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
            <OrgSelector lockToOwnOrg />
            <ToolbarButton
              icon={SortIcon}
              label={hospitalsSortOptionLabel(sortOption)}
              onClick={() => setSortDialogOpen(true)}
            />
            <ToolbarButton
              icon={FilterIcon}
              label="Filters"
              badgeCount={activeFilterCount}
              onClick={() => setFilterDialogOpen(true)}
            />
            {hasPermission("hospitals", "create") && (
              <Button
                onClick={() => setBulkUploadDialogOpen(true)}
                variant="outlined"
                startIcon={<UploadFileRounded sx={{ height: 18, width: 18 }} />}
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                }}
              >
                Bulk Upload
              </Button>
            )}
            {hasPermission("hospitals", "create") && (
              <Button
                onClick={() => navigate(hospitals_add)}
                variant="contained"
                disableElevation
                startIcon={<AddRounded sx={{ height: 18, width: 18 }} />}
                sx={{
                  bgcolor: purple,
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                  "&:hover": { bgcolor: "#7A2074" },
                }}
              >
                Add Hospital
              </Button>
            )}
          </Box>
        </Box>

        {isLoading ? (
          <Box display="flex" justifyContent="center" py={8}>
            <Loading />
          </Box>
        ) : isError ? (
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            py={8}
            gap={0.5}
          >
            <Typography fontWeight={600} color={black}>
              Something went wrong
            </Typography>
            <Typography variant="body2" color={greyEnabled}>
              We couldn't load the hospitals list. Please try again.
            </Typography>
          </Box>
        ) : showEmpty ? (
          <TableEmptyState
            message="No hospitals found"
            hint="Try adjusting your search or filters, or add a new hospital."
          />
        ) : (
          <Box mt={1}>
            <TableHeaderRow
              labels={[
                { label: "Sl. No.", flex: 0.2 },
                { label: "Hospital Id", flex: 0.35 },
                { label: "Hospital", flex: 2.45 },
                { label: "Actions" },
              ]}
              lastColumnWidth={150}
              align="left"
              bordered
            />
            {list.map((item, index) => (
              <TableRow key={item.id} bordered>
                <TableCell label="Sl. No." flex={0.2} bordered>
                  <Typography fontSize={14} color={black} noWrap>
                    {(page - 1) * pageSize + index + 1}
                  </Typography>
                </TableCell>
                <TableCell label="Hospital Id" flex={0.35} bordered>
                  <Typography fontSize={14} color={black} noWrap>
                    {item.id}
                  </Typography>
                </TableCell>
                <TableCell label="Hospital" flex={2.45} bordered>
                  <Box display="flex" alignItems="center" flexWrap="wrap" gap={1}>
                    <Typography fontWeight={600} color={black} noWrap>
                      {item.name}
                    </Typography>
                    {item.hospitalType && (
                      <Chip
                        label={capitalize(item.hospitalType)}
                        size="small"
                        sx={{ bgcolor: purpleLight, color: purple, fontWeight: 600, height: 20 }}
                      />
                    )}
                    <Chip
                      label={item.isVerified ? "Verified" : "Not Verified"}
                      size="small"
                      sx={{
                        bgcolor: item.isVerified ? greenLight : "#F1E9F1",
                        color: item.isVerified ? green : greyEnabled,
                        fontWeight: 600,
                        height: 20,
                      }}
                    />
                  </Box>
                  <Typography variant="body2" color={greyEnabled} noWrap>
                    {item.amiHospitalId ? `AMI ID: ${item.amiHospitalId} · ` : ""}
                    {[item.address, item.contactNo].filter(Boolean).join(" · ") || "-"}
                  </Typography>
                </TableCell>
                <TableCell isLast lastColumnWidth={150} bordered>
                  <Box display="flex" justifyContent={{ xs: "flex-start", sm: "flex-end" }} gap={0.5}>
                    <Tooltip title="View details">
                      <IconButton
                        onClick={() => navigate(`${hospitals_view}/${item.id}`)}
                        sx={{ bgcolor: "#E7F5EC", "&:hover": { bgcolor: "#D6EEE0" } }}
                      >
                        <VisibilityRounded sx={{ color: "#1E6446", fontSize: 20 }} />
                      </IconButton>
                    </Tooltip>
                    {hasPermission("hospitals", "update") && (
                      <Tooltip title="Edit hospital">
                        <IconButton
                          onClick={() => navigate(`${hospitals_edit}/${item.id}`)}
                          sx={{ bgcolor: purpleLight, "&:hover": { bgcolor: "#F0D9F0" } }}
                        >
                          <EditRounded sx={{ color: purple, fontSize: 20 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                    {hasPermission("hospitals", "delete") && (
                      <Tooltip title="Delete hospital">
                        <IconButton
                          onClick={() => setDeleteTarget(item)}
                          sx={{ bgcolor: redLight, "&:hover": { bgcolor: "#FBD9D6" } }}
                        >
                          <DeleteOutlineRounded sx={{ color: red, fontSize: 20 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </Box>
        )}
      </CardContent>

      {showList && (
        <CardActions sx={{ px: 4, pb: 3 }}>
          <Box width="100%">
            <TableFooterPagination
              count={count}
              page={page - 1}
              onPageChange={handleChangePage}
              summaryText={customText}
              rowsPerPage={pageSize}
              rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
              onRowsPerPageChange={setPageSize}
            />
          </Box>
        </CardActions>
      )}

      <DeleteHospitalDialog
        open={!!deleteTarget}
        hospitalName={deleteTarget?.name}
        isDeleting={isDeleting}
        onClose={() => !isDeleting && setDeleteTarget(null)}
        onConfirm={handleDeleteConfirm}
      />

      <SortHospitalsPopup
        open={sortDialogOpen}
        onClose={() => setSortDialogOpen(false)}
        sortOption={sortOption}
        setSortOption={setSortOption}
      />

      <BulkUploadHospitalsDialog open={bulkUploadDialogOpen} onClose={() => setBulkUploadDialogOpen(false)} />

      {filterDialogOpen && (
        <FilterHospitals
          open={filterDialogOpen}
          onClose={() => setFilterDialogOpen(false)}
          hospitalType={filters.hospitalType}
          stateId={filters.stateId}
          districtId={filters.districtId}
          fromDate={filters.fromDate}
          toDate={filters.toDate}
          onApply={setFilters}
        />
      )}
    </HospitalLayout>
    </MainLayout>
  );
}
